import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';

import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { useCallback, useEffect, useMemo, useState } from 'react';

export const useCommonActsKC2GroupDelete = <Data, Variables = any>({
    refreshActListData,
    deleteQuery,
}: {
    refreshActListData: () => void;
    deleteQuery: string | TypedDocumentNode<Data, Variables>;
}) => {
    const [groupIdForDelete, setGroupIdForDelete] = useState<number | null>(null);

    const [reset] = useResetStoreData({
        requestId: 'actsKc2GroupDelete',
    });

    const onSuccess = useCallback(() => {
        refreshActListData();
        reset(undefined);
    }, [refreshActListData, reset]);

    const [, , , deleteGroupRequest] = useRequest<Data, Variables>({
        requestId: 'actsKc2GroupDelete',
        query: deleteQuery,
        withEffect: false,
        dependenciesUpdater: onSuccess,
    });

    const variables = useMemo(() => ({ actKC2GroupId: groupIdForDelete }), [groupIdForDelete]);

    const { handleOpenConfirmationDialog, isOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionQuery: deleteGroupRequest,
            variables: variables as Variables,
        });

    const handleDeleteGroup = useCallback(
        (groupId: number) => {
            setGroupIdForDelete(groupId);
            handleOpenConfirmationDialog();
        },
        [handleOpenConfirmationDialog],
    );

    useEffect(() => {
        if (!isOpenConfirmationDialog) {
            setGroupIdForDelete(null);
        }
    }, [isOpenConfirmationDialog]);

    return {
        handleDeleteGroup,
        isDeleteOpened: isOpenConfirmationDialog,
        confirmDeletion: handleActionQuery,
        cancelDeletion: handleCloseConfirmationDialog,
    };
};
